import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Crown, Gift, Heart, Star, Newspaper, Tv, Mic, Sparkles, Share2, Users, TrendingUp, Lock, Loader2, ShieldCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RedeemModal from "../components/RedeemModal";
import PricingModal from "../components/PricingModal";
import DailyBriefModal from "../components/DailyBriefModal";
import useNewsezeUser from "../lib/useNewsezeUser";
import { fetchPricing } from "../lib/api";

const HOWS = [
  { icon: Newspaper, label: "Read an article", per: 10, daily: 500 },
  { icon: Tv, label: "Watch a video brief", per: 100, daily: 800 },
  { icon: Mic, label: "Listen to a podcast", per: 150, daily: 1500 },
  { icon: Sparkles, label: 'Open the AI "Brief Me"', per: 50, daily: 150 },
  { icon: Star, label: "Rate a story", per: 5, daily: 60 },
  { icon: TrendingUp, label: "Pin a watchlist ticker", per: 25, daily: 100 },
  { icon: Share2, label: "Share a story", per: 15, daily: 90 },
  { icon: Users, label: "Refer a friend (one-time)", per: 1000, daily: null },
];

const BONUSES = [
  { title: "Daily Streak Bonus", per: "+50 / day", sub: "Read at least 1 story every day. Streak resets at 30 — and pays a 5,000-credit jackpot ($5) every full month." },
  { title: "Pro 2× Multiplier", per: "2× all credits", sub: "Pro members double every credit they earn — articles, videos, briefs, podcasts, even referrals." },
  { title: "Weekend Booster", per: "+25% Sat/Sun", sub: "Every Saturday & Sunday, every credit earns 25% more — automatically." },
  { title: "First-Week Welcome", per: "+1,000 credits", sub: "New subscribers get a 1,000-credit welcome bonus when they read 5 stories in their first 7 days." },
  { title: "Refer-the-Family", per: "+5,000 jackpot", sub: "Bring 5 friends in 30 days and earn a 5,000-credit jackpot — that's $5 cash on top of your per-referral pay." },
  { title: ".edu Free Pro", per: "Lifetime Pro for $0", sub: "Verified students earn at the Pro 2× rate forever — no payment required while you're enrolled." },
];

const REDEMPTIONS = [
  { icon: ShieldCheck, title: "Unlock a Member Benefit", credits: 2500, badge: "Open to all · Lower threshold", color: "from-[#7a5b00] to-[#b8860b]",
    sub: "Spend credits to unlock any premium partner benefit from our 100+ Hub — insurance discounts, financial tools, travel, SaaS, learning, and more." },
  { icon: Gift, title: "$5 Visa / Amazon Gift Card", credits: 5000, badge: "US only · 14-day vest", color: "from-[#0e2d63] to-[#1e40af]",
    sub: "Choose Amazon, Visa Virtual, Target, Starbucks, and 1000+ other brands. Vested credits only. KYC required above $25 lifetime." },
  { icon: Heart, title: "$5 Charity Donation", credits: 5000, color: "from-[#7f1d1d] to-[#b91c1c]", badge: "Open to all countries",
    sub: "Donate to Red Cross, Wikimedia, RAINN, Feeding America, or Doctors Without Borders. 100% goes to the charity — we cover the fees." },
];

export default function Rewards() {
  const { email, credits, refreshCredits } = useNewsezeUser();
  const [pricing, setPricing] = useState(null);
  const [showRedeem, setShowRedeem] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showBrief, setShowBrief] = useState(false);

  useEffect(() => { fetchPricing().then(setPricing).catch(() => {}); }, []);

  const balance = credits?.balance ?? 0;
  const usd = credits?.usd_value ?? 0;
  const lifetime = credits?.lifetime ?? 0;
  const tier = credits?.tier || "free";

  return (
    <>
      <Header onOpenBrief={() => setShowBrief(true)} />
      <main className="max-w-[1200px] mx-auto px-5 py-8">
        {/* Hero */}
        <section className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-8 md:p-10 relative overflow-hidden" data-testid="rewards-hero">
          <div className="relative z-10 max-w-[800px]">
            <div className="inline-flex items-center gap-2 text-[var(--gold)] text-[11px] uppercase tracking-[0.2em] font-black mb-3">
              <Coins size={14} /> The biggest rewards program in news
            </div>
            <h1 className="text-[36px] md:text-[52px] font-black tracking-[-0.025em] leading-[1.0]">
              News with <span className="text-[var(--gold)]">Rewards.</span>
            </h1>
            <div className="text-[18px] md:text-[22px] font-black tracking-tight mt-2 opacity-95">
              Honest news. <span className="text-[var(--gold)]">That rewards you for reading.</span>
            </div>
            <p className="text-[15px] md:text-[17px] opacity-95 mt-4 leading-snug max-w-[660px]">
              Earn real value for every story you read. Convert to <b className="text-[var(--gold)]">Newseze Pro months</b>, <b className="text-[var(--gold)]">premium member benefits</b>, <b className="text-[var(--gold)]">gift cards</b> (US), or <b className="text-[var(--gold)]">charity donations</b>. Pro members double everything. Safe by design — no cash, no sweepstakes, no fraud surface.
            </p>

            {email ? (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-[640px]" data-testid="rewards-stats">
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">Balance</div>
                  <div className="text-[28px] font-black tabular-nums">${usd.toFixed(2)}</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">Credits</div>
                  <div className="text-[28px] font-black tabular-nums">{balance.toLocaleString()}</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">Lifetime</div>
                  <div className="text-[28px] font-black tabular-nums">{lifetime.toLocaleString()}</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">Tier</div>
                  <div className="text-[20px] font-black uppercase tracking-tight">{tier === "pro" ? "Pro" : tier === "lite" ? "Lite" : "Free"}</div>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowBrief(true)} className="btn gold mt-6 text-[14px]" data-testid="rewards-cta">
                <Lock size={14} /> Start earning free — no credit card
              </button>
            )}

            <div className="flex flex-wrap gap-2 mt-5">
              {email && balance >= 5000 && (
                <button onClick={() => setShowRedeem(true)} className="btn gold" data-testid="rewards-redeem">
                  <Gift size={14} /> Redeem credits
                </button>
              )}
              {email && tier !== "pro" && (
                <button onClick={() => setShowPricing(true)} className="btn" data-testid="rewards-upgrade">
                  <Crown size={14} /> Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </section>

        {/* How to earn */}
        <section className="mt-10" data-testid="rewards-earn">
          <div className="section-title">
            <span className="pip" />
            <h2>How to earn</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--muted)]">1 credit = $0.001 · $5 cashout threshold</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {HOWS.map(({ icon: Icon, label, per, daily }) => (
              <div key={label} className="rounded-xl border border-[var(--line)] bg-white p-4 hover:border-[var(--blue)] transition" data-testid={`earn-${label.split(" ")[0].toLowerCase()}`}>
                <Icon size={18} className="text-[var(--blue)] mb-2" />
                <div className="text-[14px] font-black text-[var(--ink)]">{label}</div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-[24px] font-black text-[var(--gold)] tabular-nums">+{per}</span>
                  <span className="text-[12px] text-[var(--muted)]">credits</span>
                </div>
                {daily && <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mt-1">Up to {daily.toLocaleString()}/day</div>}
              </div>
            ))}
          </div>
        </section>

        {/* Bonus boosters — the "massive" part */}
        <section className="mt-12" data-testid="rewards-bonuses">
          <div className="section-title">
            <span className="pip" />
            <h2>Bonus Boosters · Stack the multipliers</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--muted)]">Generous · Stackable · Automatic</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {BONUSES.map((b) => (
              <div key={b.title} className="rounded-xl bg-gradient-to-br from-[#fff9ed] to-[#fff] border-2 border-[var(--gold)] p-4" data-testid={`bonus-${b.title.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Booster</div>
                <div className="text-[15px] font-black text-[var(--ink)] mt-1">{b.title}</div>
                <div className="text-[20px] font-black text-[#0e2d63] tabular-nums mt-1">{b.per}</div>
                <p className="text-[12px] text-[var(--muted)] leading-snug mt-2">{b.sub}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Redeem */}
        <section className="mt-12" data-testid="rewards-redeem-section">
          <div className="section-title">
            <span className="pip" />
            <h2>How to redeem</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--muted)]">Cash out anytime at 5,000 credits</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REDEMPTIONS.map(({ icon: Icon, title, credits: cost, sub, badge, color }) => (
              <div key={title} className={`rounded-xl bg-gradient-to-br ${color} text-white p-5 relative overflow-hidden`} data-testid={`redeem-card-${title.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-start gap-3">
                  <div className="bg-white/15 rounded-lg p-2"><Icon size={20} /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[16px] font-black">{title}</div>
                      {badge && <span className="text-[9px] uppercase tracking-[0.16em] font-black bg-[var(--gold)] text-[#1d1303] px-1.5 py-0.5 rounded">{badge}</span>}
                    </div>
                    <p className="text-[12.5px] opacity-90 leading-snug mt-1">{sub}</p>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--gold)] mt-3">{cost.toLocaleString()} credits</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            {email && balance >= 5000 ? (
              <button onClick={() => setShowRedeem(true)} className="btn gold text-[14px]" data-testid="rewards-redeem-bottom">
                <Gift size={14} /> Redeem now — you have ${usd.toFixed(2)}
              </button>
            ) : email ? (
              <p className="text-[13px] text-[var(--muted)]">
                {(5000 - balance).toLocaleString()} more credits to reach your first $5 cashout.
              </p>
            ) : (
              <button onClick={() => setShowBrief(true)} className="btn gold text-[14px]" data-testid="rewards-redeem-signup">
                <Lock size={14} /> Subscribe free to start earning
              </button>
            )}
          </div>
        </section>

        {/* Safety & fraud-resistance — the trust spine */}
        <section className="mt-12 rounded-2xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-6 md:p-8" data-testid="rewards-safety">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-black text-[var(--gold)]">
            <ShieldCheck size={14} /> Safe by design
          </div>
          <h2 className="text-[24px] md:text-[28px] font-black tracking-[-0.02em] mt-2">Generous to readers. Fraud-resistant to bad actors.</h2>
          <p className="text-[14px] md:text-[15px] opacity-90 mt-2 max-w-[760px] leading-snug">
            Newseze pays real money. To protect the program from organized fraud and keep paying readers for the long haul, we use five guardrails — all transparent, all explained below.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {[
              { title: "No cash. No sweepstakes.", body: "Credits convert to Pro months, premium member benefits, gift cards, or charity — never to direct cash. Removes the fraud incentive entirely." },
              { title: "No drama. No conspiracies.", body: "Every story is scored for drama and conspiracy. Sensational clickbait or fringe theories never reach Newseze readers." },
              { title: "14-day credit vesting", body: "Every credit ripens 14 days before it can be redeemed for gift cards. Pro credit, member benefits, and charity work immediately." },
              { title: "Benefits-first economy", body: "Spend credits on real partner benefits — insurance discounts, financial tools, travel, SaaS, learning — instead of withdrawing dollars." },
              { title: "US-only for gift cards", body: "Foreign accounts earn Pro credit, member benefits, and charity — no gift cards. Removes cross-border fraud entirely." },
              { title: "Behavioral fraud scoring", body: "Burst-rate, IP diversity, read-pace, and click signatures auto-flag suspicious accounts before they can redeem. Real readers always pass." },
            ].map((g) => (
              <div key={g.title} className="bg-white/10 rounded-xl p-4 border border-white/15" data-testid={`safety-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[14px] font-black text-[var(--gold)]">{g.title}</div>
                <p className="text-[12.5px] text-white/85 leading-snug mt-1.5">{g.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pay monthly with credits */}
        <section className="mt-12 rounded-2xl bg-[var(--sky)] border border-[var(--sky-2)] p-6 md:p-8" data-testid="rewards-pay-monthly">
          <div className="flex items-start gap-4 flex-wrap">
            <Crown size={28} className="text-[var(--gold)] shrink-0" />
            <div className="flex-1 min-w-[280px]">
              <h2 className="text-[22px] font-black tracking-[-0.02em] text-[var(--ink)]">Use your credits to pay for Pro every month.</h2>
              <p className="text-[14px] text-[var(--ink)] mt-2 leading-snug">
                9,990 credits = 1 month of <b>Newseze Pro</b> ($9.99 value). If you read ~50 articles and watch ~3 video briefs per day, you can pay for Pro entirely with the credits you earn — never touch your wallet. Pro members earn 2× credits, making the loop even faster.
              </p>
              <div className="flex gap-2 mt-4">
                {email && balance >= 9990 ? (
                  <button onClick={() => setShowRedeem(true)} className="btn gold" data-testid="redeem-promonth-cta">
                    Pay this month with credits
                  </button>
                ) : (
                  <button onClick={() => setShowPricing(true)} className="btn primary" data-testid="rewards-see-pro">
                    See what Pro unlocks
                  </button>
                )}
                <Link to="/" className="btn">Start reading</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Quality promise */}
        <section className="mt-12" data-testid="rewards-quality">
          <div className="section-title">
            <span className="pip" />
            <h2>Our promise on ads + video</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { title: "Quality ads only", body: "We screen advertisers. No misleading claims, no fake news, no political attack ads. If an ad disrespects our readers, we kill it." },
              { title: "No extreme video", body: "Every video we publish passes editorial review. No raw violence, no gore, no extremism. Period." },
              { title: "Respectful framing", body: "Every story is scored for respect. Anything that mocks or demeans public officials of any party is blocked from the front page." },
            ].map((p) => (
              <div key={p.title} className="rounded-xl border border-[var(--line)] bg-white p-4">
                <div className="text-[14px] font-black text-[var(--ink)]">{p.title}</div>
                <p className="text-[12.5px] text-[var(--muted)] leading-snug mt-2">{p.body}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />

      {showRedeem && (
        <RedeemModal email={email} credits={credits} onClose={() => setShowRedeem(false)} onDone={() => refreshCredits()} />
      )}
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} onNeedSubscribe={() => setShowBrief(true)} />}
      {showBrief && <DailyBriefModal onClose={() => setShowBrief(false)} />}
    </>
  );
}
